import { Options } from 'ky';
import { parse } from 'qs';
import * as R from 'ramda';
import config from 'config';
import { useAuthenticatedApiClient } from 'utils/apiClient/base';
import { toString } from 'utils/own-fetch';
import {
  ApplicationApiCallArguments,
  TokenApiCallArguments,
  ScopeType,
  AuthApps,
  Token,
  App,
} from './types';

const amClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api/auth/`,
};

type CredentialsMode = 'omit' | 'same-origin' | 'include';
type RequestMode = 'cors' | 'no-cors' | 'same-origin';

const customFormHeader = {
  'Content-Type': 'application/x-www-form-urlencoded',
};

const customJsonHeader = {
  'Content-Type': 'application/json',
};
const pickUrlParams = R.compose(R.last, R.split('?'));
const pickCodeFromUrl: any = R.compose(
  R.prop('code'),
  parse,
  pickUrlParams,
  R.prop('url') as any
);

const redirectUri = window.location.origin;

/* eslint-disable import/prefer-default-export */
export const useAppsManagementApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const appsManagementApiClient = authenticatedApiClient.extend(amClientOpts);

  return {
    fetchApplications: (params?: { page?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) {
        searchParams.append('page', params.page);
      }
      const url = searchParams.toString()
        ? `applications/?${searchParams.toString()}`
        : 'applications/';
      return appsManagementApiClient.get(url).json<AuthApps>();
    },
    createApplication: ({
      name,
    }: Omit<ApplicationApiCallArguments, 'appId'>) => {
      const payload = {
        name,
        redirect_uris: redirectUri,
        client_type: 'confidential',
        authorization_grant_type: 'authorization-code',
      };
      return appsManagementApiClient
        .post('applications/', {
          headers: customJsonHeader,
          body: JSON.stringify(payload),
        })
        .json<App>();
    },
    deleteApplication: ({ appId }: Omit<ApplicationApiCallArguments, 'name'>) =>
      appsManagementApiClient.delete(`applications/${appId}`),
    fetchTokens: ({ appId }: Omit<ApplicationApiCallArguments, 'name'>) =>
      appsManagementApiClient
        .get(`applications/${appId}/tokens/`)
        .json<Array<Token>>(),
    createToken: async ({
      client_id,
      client_secret,
      scope,
    }: Omit<TokenApiCallArguments, 'code' | 'accessToken'> & {
      scope: any;
    }) => {
      const payload1 = {
        client_id,
        response_type: 'code',
        redirect_uri: redirectUri,
        allow: true,
        scope,
      };
      let location;
      const options = {
        headers: customFormHeader,
        throwHttpErrors: false,
        mode: 'cors' as RequestMode,
        credentials: 'include' as CredentialsMode,
        body: toString(payload1),
        hooks: {
          afterResponse: [
            async (req: any, opt: any, res: any) => {
              location = res;
            },
          ],
        },
      };
      try {
        const data = await appsManagementApiClient.post('authorize/', options);

        const code = pickCodeFromUrl(data);

        const payload2 = {
          code,
          client_id,
          client_secret,
          redirect_uri: redirectUri,
          grant_type: 'authorization_code',
        };

        return appsManagementApiClient
          .post('token/', {
            headers: customFormHeader,
            body: toString(payload2),
          })
          .json();
      } catch (error) {
        //retry with captured response in afterResponse hook
        const code = pickCodeFromUrl(location);
        const payload2 = {
          code,
          client_id,
          client_secret,
          redirect_uri: redirectUri,
          grant_type: 'authorization_code',
        };

        return appsManagementApiClient
          .post('token/', {
            headers: customFormHeader,
            body: toString(payload2),
          })
          .json();
      }
    },
    revokeToken: ({
      client_id,
      client_secret,
      accessToken,
    }: Omit<TokenApiCallArguments, 'code'>) => {
      const payload = {
        client_id,
        client_secret,
        token: accessToken,
      };

      const options = {
        headers: customFormHeader,
        body: toString(payload),
        ignoreHandler: true,
      };

      return appsManagementApiClient.post('revoke_token/', options);
    },

    fetchScopes: () => appsManagementApiClient.get('scopes/').json<ScopeType>(),
  };
};
